// What a deal promised: a label, a date, whether it's delivered, and optionally the
// work that satisfies it.
//
// The important thing this shape buys is ORDER OF EXISTENCE. You can promise "semantic
// map export, September 1" the day you sign, with nothing built and no work record to
// point at, and attach the work later — or never, if the promise is a workshop or a
// report. The work never carries the date or the tick: the promise has a status, the
// thing doesn't.

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { todayIso } from '../lib/format'
import { newId } from '../lib/ids'
import { daysSince } from '../lib/metrics'
import { commitmentsForDeal, deliveryOf, titleOf, type CommitmentRecord } from '../lib/reducer'
import { useStore } from '../lib/store'
import { ArchivedRow, ArchivedToggle, ConfirmAction, EditableText } from './common'
import { Icon } from './Icon'
import { RefPicker } from './RefPicker'

export function Commitments({ dealId }: { dealId: string }) {
  const { state, dispatch, version } = useStore()
  void version
  const all = commitmentsForDeal(state, dealId, true)
  const items = all.filter((c) => !c.archived)
  const gone = all.filter((c) => c.archived)
  const [adding, setAdding] = useState('')
  const [showArchived, setShowArchived] = useState(false)

  const add = () => {
    const label = adding.trim()
    if (!label) return
    void dispatch({
      type: 'commitment.create',
      subject: newId('com'),
      data: { deal: dealId, label },
    })
    setAdding('')
  }

  return (
    <div>
      {items.length > 0 && (
        <div className="rows">
          {items.map((c) => (
            <Row key={c.id} c={c} />
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center gap-2">
        <Icon name="Plus" size={12} style={{ color: 'var(--faint)' }} />
        <input
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') add()
            if (e.key === 'Escape') setAdding('')
          }}
          onBlur={add}
          placeholder="What was promised"
          className="text-body flex-1 bg-transparent outline-none"
        />
        {adding.trim() && <span className="meta">Enter to add</span>}
        <span className="ml-auto">
          <ArchivedToggle
            count={gone.length}
            on={showArchived}
            onToggle={() => setShowArchived((v) => !v)}
          />
        </span>
      </div>

      {/* A promise that was removed is still a promise that was made, and a deal
          whose deliverables can only go one way cannot be corrected. */}
      {showArchived && gone.length > 0 && (
        <div className="rows mt-2">
          {gone.map((c) => (
            <ArchivedRow
              key={c.id}
              onRestore={() => void dispatch({ type: 'commitment.restore', subject: c.id })}
            >
              {c.label || 'Untitled'}
              {c.due ? <span className="meta num"> · {c.due}</span> : null}
            </ArchivedRow>
          ))}
        </div>
      )}
    </div>
  )
}

function Row({ c }: { c: CommitmentRecord }) {
  const { state, dispatch } = useStore()
  const navigate = useNavigate()
  const upd = (data: Record<string, unknown>) =>
    void dispatch({ type: 'commitment.update', subject: c.id, data })

  const done = !!c.deliveredAt
  const late = !done && !!c.due && daysSince(c.due) > 0

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      {/* One click to deliver, one to undo. Delivering stamps today's date, which is
          what makes "3 of 5 · next Sep 1" true without a second field to maintain. */}
      <button
        type="button"
        onClick={() => upd({ deliveredAt: done ? '' : todayIso() })}
        title={done ? `Delivered ${c.deliveredAt} · click to reopen` : 'Mark delivered'}
        aria-pressed={done}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border"
        style={{
          borderColor: done ? 'var(--good)' : late ? 'var(--crit)' : 'var(--border)',
          background: done ? 'color-mix(in srgb, var(--good) 18%, transparent)' : 'transparent',
          color: done ? 'var(--good)' : 'transparent',
        }}
      >
        <Icon name="Check" size={12} />
      </button>

      <span
        className="text-body min-w-[10rem] flex-1"
        style={done ? { color: 'var(--muted)' } : undefined}
      >
        <EditableText
          value={c.label}
          placeholder="What was promised"
          onSave={(label) => upd({ label })}
          style={{ marginLeft: -8 }}
        />
      </span>

      {/* The work, if it exists yet. Empty is a normal, permanent state. */}
      {c.work ? (
        <button
          type="button"
          onClick={() => navigate(`/e/${c.work}`)}
          className="rounded-inline inline-flex max-w-[14rem] items-center gap-1 truncate px-1.5 py-0.5"
          style={{
            background: 'var(--work-soft)',
            color: 'var(--marine)',
            fontFamily: 'var(--mono)',
            fontSize: 'var(--text-micro)',
          }}
          title={`Satisfied by ${titleOf(state, c.work)}`}
        >
          <Icon name="Boxes" size={12} /> {titleOf(state, c.work)}
        </button>
      ) : (
        <RefPicker
          kind="project"
          value=""
          placeholder="link project"
          onPick={(work) => upd({ work })}
        />
      )}
      {c.work && (
        <button
          type="button"
          onClick={() => upd({ work: '' })}
          className="meta"
          title="Unlink the project (the promise stays)"
        >
          <Icon name="X" size={12} />
        </button>
      )}

      <span
        className="num shrink-0"
        style={{
          fontSize: 'var(--text-label)',
          color: done ? 'var(--good)' : late ? 'var(--crit)' : 'var(--warn)',
        }}
        title={done ? 'delivered' : late ? `${daysSince(c.due)} days past` : 'due'}
      >
        <EditableText
          value={done ? c.deliveredAt : c.due}
          type="date"
          placeholder="date"
          onSave={(v) => upd(done ? { deliveredAt: v } : { due: v })}
          style={{ fontFamily: 'var(--mono)', fontSize: 'var(--text-label)' }}
        />
      </span>

      <ConfirmAction
        icon="X"
        title="Remove this deliverable"
        confirmLabel="Remove"
        onConfirm={() => void dispatch({ type: 'commitment.archive', subject: c.id })}
      />
    </div>
  )
}

/** "3 of 5 · next Sep 1", for the section header. */
export function DeliveryNote({ dealId }: { dealId: string }) {
  const { state, version } = useStore()
  void version
  const d = deliveryOf(state, dealId)
  if (d.items.length === 0) return null
  return (
    <span className="meta">
      {d.delivered} of {d.items.length} delivered
      {d.next ? ` · next ${d.next.due}` : ''}
    </span>
  )
}
